import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Splide, SplideSlide } from '@splidejs/react-splide';

import SmartImage from 'dpl/components/SmartImage';
import { isMobileUA } from 'dpl/shared/utils';
import Icon from 'dpl/common/components/Icon';

const IS_MOBILE_UA = isMobileUA();
const DEFAULT_SPLIDE_OPTIONS = {
  arrows: false,
  dragMinThreshold: 20,
  flickMaxPages: 1,
  flickPower: 300,
  keyboard: 'focused',
  pagination: false,
  perPage: 1,
  snap: true
};
const IMAGE_SIZE = 512;

export default function ParentModalGallery({ gallery }) {
  const splideRef = useRef(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const isNextButtonDisabled = activeSlideIndex >= gallery.length - 1;
  const isPrevButtonDisabled = activeSlideIndex === 0;

  function handleActiveSlide(slide) {
    setActiveSlideIndex(slide.index);
  }

  if (gallery.length === 0) {
    return null;
  }

  if (IS_MOBILE_UA) {
    return (
      <div className="nh5 mt4 relative">
        <Splide
          ref={splideRef}
          options={{
            ...DEFAULT_SPLIDE_OPTIONS,
            drag: gallery.length > 1
          }}
          onActive={handleActiveSlide}
        >
          {gallery.map(photo => (
            <SplideSlide
              key={photo.id}
              className="aspect-ratio aspect-ratio--1x1"
            >
              <div className="aspect-ratio--object">
                <SmartImage
                  className="w-100 h-100 object-cover"
                  wrapperOnlyClassName="overflow-hidden"
                  src={photo.url}
                  lazy
                />
              </div>
            </SplideSlide>
          ))}
        </Splide>
        {gallery.length > 1 && (
          <p className="absolute right-0 bottom-0 mv4 mh4 bg-dark-transparent br-pill inline-flex items-center padding-v-6 ph3 font-14 white lh-body">
            {(activeSlideIndex ?? 0) + 1}/{gallery.length}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={classnames('mt4 relative', {
        nh8: gallery.length > 1
      })}
    >
      {gallery.length === 1 ? (
        <div className="aspect-ratio aspect-ratio--7x5">
          <div className="aspect-ratio--object">
            <SmartImage
              className="w-100 h-100 object-cover"
              wrapperOnlyClassName="br3 overflow-hidden"
              src={gallery[0].url}
            />
          </div>
        </div>
      ) : (
        <>
          <Splide
            ref={splideRef}
            options={{
              ...DEFAULT_SPLIDE_OPTIONS,
              gap: 8,
              fixedWidth: IMAGE_SIZE,
              autoWidth: true
            }}
            onActive={handleActiveSlide}
          >
            {gallery.map(photo => (
              <SplideSlide key={photo.id}>
                <SmartImage
                  className="w-100 h-100 object-cover"
                  src={photo.url}
                  width={`${IMAGE_SIZE}px`}
                  height={`${IMAGE_SIZE}px`}
                  crop
                />
              </SplideSlide>
            ))}
          </Splide>
          <div className="transform-center left-0 ml6">
            <button
              type="button"
              className={classnames('bg-white ba b--default br-100 secondary', {
                'disabled b--surface-disabled': isPrevButtonDisabled
              })}
              disabled={isPrevButtonDisabled}
              onClick={() => splideRef.current.go('<')}
            >
              <div className="flex items-center justify-center ph1 pv1">
                <Icon name="fetch-arrow-left-md" />
              </div>
            </button>
          </div>
          <div className="transform-center right-0 mr6">
            <button
              type="button"
              className={classnames('bg-white ba b--default br-100 secondary', {
                'disabled b--surface-disabled': isNextButtonDisabled
              })}
              disabled={isNextButtonDisabled}
              onClick={() => splideRef.current.go('>')}
            >
              <div className="flex items-center justify-center ph1 pv1">
                <Icon name="fetch-arrow-right-md" />
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

ParentModalGallery.propTypes = {
  gallery: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      url: PropTypes.string
    })
  ).isRequired
};
